/*
Shared chrome for the inspection-scheduling page (STREAM-38, v3 design).

Every screen — wizard step, status screen, branch, edge — renders inside the
same shell: Reggora trust header (teal), optional back row, content column
with a semantic status pill, and the constant "Secured by Reggora" trust bar.
Status pill colors are fixed/semantic and never lender-derived; the lender is
named in copy and the trust bar only.
*/

import React from 'react'

const SUPPORT_MAILTO = 'mailto:support@reggora.com'

export const ShieldIcon = () => (
  <svg
    width='14' height='14' viewBox='0 0 24 24' fill='none' aria-hidden='true'
    stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'
  >
    <path d='M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z' />
    <path d='M8.5 12l2.5 2.5 4.5-5' />
  </svg>
)

// Reggora trust header — V1 ships the Reggora skin only; the lender is named
// in the landing lede and the trust bar, never in the header band.
export const TrustHeader = () => (
  <div className='isp-header'>
    <div className='isp-header-row'>
      <div className='isp-mark' aria-hidden='true'>R</div>
      <div>
        <div className='isp-header-name'>Property inspection</div>
        <div className='isp-header-sub'>Scheduling by Reggora</div>
      </div>
    </div>
  </div>
)

export const TrustBar = ({ lenderName }) => (
  <div className='isp-trust'>
    <ShieldIcon />
    <div className='isp-trust-text'>
      Secured by <b>Reggora</b>
      {lenderName ? ' on behalf of ' + lenderName : ''}.{' '}
      Questions? <a href={SUPPORT_MAILTO}>Contact support</a>
    </div>
  </div>
)

// Semantic status pill: dot + uppercase label. Tones map to the fixed
// --st-* palette (action / progress / confirm / dayof / complete / reggora).
export const StatusPill = ({ tone, label }) => (
  <div className={'isp-pill ' + tone} data-testid='status-pill'>
    <span className='dot' />
    {label}
  </div>
)

export const BackRow = ({ onBack }) => (
  <div className='isp-navrow'>
    <button className='isp-back' onClick={onBack} aria-label='Go back'>
      <svg
        viewBox='0 0 24 24' fill='none' aria-hidden='true'
        stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'
      >
        <path d='M15 5l-7 7 7 7' />
      </svg>
      <span>Back</span>
    </button>
  </div>
)

export const InfoCard = ({ children }) => (
  <div className='isp-card'>{children}</div>
)

export const InfoRow = ({ label, sub, children }) => (
  <div className='isp-row'>
    <span className='isp-row-label'>{label}</span>
    <span className='isp-row-value'>
      {children}
      {sub && <span className='isp-row-sub'>{sub}</span>}
    </span>
  </div>
)

export const SuccessRing = () => (
  <div className='isp-success' aria-hidden='true'>
    <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.6' strokeLinecap='round' strokeLinejoin='round'>
      <path d='M5 12l4.5 4.5L19 7' />
    </svg>
  </div>
)

// Common shell: header, optional back row, content, trust bar.
export const Screen = ({ lenderName, onBack, centered, testId, children }) => (
  <div className='inspection-scheduling' data-testid={testId}>
    <TrustHeader />
    {onBack && <BackRow onBack={onBack} />}
    <div className={centered ? 'isp-content isp-center' : 'isp-content'}>
      {children}
    </div>
    <TrustBar lenderName={lenderName} />
  </div>
)
